import {executeFilter, executeValidator} from '@/library/form';
import {deepCopyObject} from '@/utils/portuna';
import {type ActionTree} from 'vuex';

const formatFilters = (formattedFilters: object, filters: object | any[], currentKey = '') =>
{
    for (let key in filters)
    {
        if (filters.hasOwnProperty(key))
        {
            if (key === '__filterList')
            {
                continue;
            }

            if (filters[key] && typeof filters[key] === 'object' && (filters[key].__filterList || filters[key][0] && filters[key][0].__filterList))
            {
                formatFilters(formattedFilters, filters[key], (currentKey ? currentKey + '.' : currentKey) + key);
            }
            else
            {
                formattedFilters[(currentKey ? currentKey + '.' : currentKey) + key] = filters[key];
            }
        }
    }
};

const actions: ActionTree<FormState, RootState> = {
    setFilter({commit}, filters)
    {
        let formattedFilters = {};
        if (filters !== null)
        {
            formatFilters(formattedFilters, filters);
        }

        commit('reset');
        commit('setFilter', formattedFilters);
    },
    addValidator({
        commit,
        state,
    }, {
        element,
        validator,
    })
    {
        let filters = deepCopyObject(state.filter);
        let elementFilters: FilterDefinition;

        if (filters[element])
        {
            elementFilters = filters[element];

            if ('undefined' === typeof elementFilters.validators)
            {
                elementFilters.validators = [];
            }
        }
        else
        {
            elementFilters = {
                name: element,
                validators: [],
            };
        }

        let alreadyExists = false;
        for (let check of elementFilters.validators!)
        {
            if (check.name === validator.name)
            {
                alreadyExists = true;
            }
        }

        if (!alreadyExists)
        {
            if (validator.name === 'NotEmpty')
            {
                elementFilters.required = true;
            }

            if (typeof validator.toIndex === 'number' && typeof elementFilters.validators![validator.toIndex] !== 'undefined')
            {
                elementFilters.validators!.splice(validator.toIndex, 0, validator);
            }
            else
            {
                elementFilters.validators!.push(validator);
            }

            filters[element] = elementFilters;
            commit('setFilter', filters);
        }
    },
    removeValidator({
        commit,
        state,
    }, {
        element,
        name,
    })
    {
        let filters = deepCopyObject(state.filter);

        if (filters[element])
        {
            let elementFilters = filters[element];

            if ('undefined' === typeof elementFilters.validators)
            {
                return;
            }

            for (let index in elementFilters.validators)
            {
                if (!elementFilters.validators.hasOwnProperty(index))
                {
                    continue;
                }

                if (elementFilters.validators[index].name === name)
                {
                    if (name === 'NotEmpty')
                    {
                        elementFilters.required = false;
                    }

                    elementFilters.validators.splice(index, 1);
                    commit('setFilter', filters);
                    break;
                }
            }
        }
    },
    addCollection({
        commit,
        state,
    }, {
        collection,
        filters,
    })
    {
        let formattedFilters = deepCopyObject(state.filter);
        if (filters !== null)
        {
            formatFilters(formattedFilters, filters, collection);
        }
        commit('setFilter', formattedFilters);
    },
    removeCollection({
        commit,
        state,
    }, {
        collection,
    })
    {
        let filters = deepCopyObject(state.filter);

        for (let key in filters)
        {
            if (filters.hasOwnProperty(key) && key.startsWith(collection + '.'))
            {
                delete filters[key];
            }
        }

        commit('setFilter', filters);
        return;
    },
    setErrors({commit}, errors)
    {
        let formattedErrors = {};
        if (errors !== null)
        {
            const formatErrors = (errors: object | any[], currentKey = '') =>
            {
                for (let key in errors)
                {
                    if (errors.hasOwnProperty(key))
                    {
                        if (Array.isArray(errors[key]) || typeof errors[key] === 'object' && errors[key])
                        {
                            formatErrors(errors[key], (currentKey ? currentKey + '.' : currentKey) + key);
                        }
                        else
                        {
                            formattedErrors[currentKey] = errors;
                        }
                    }
                }
            };
            formatErrors(errors);
        }

        commit('setErrors', formattedErrors);
    },
    setError({commit}, {
        errorName,
        errors,
    })
    {
        commit(
            'setError',
            {
                errorName,
                errors,
            },
        );
    },
    resetError({commit}, {errorName})
    {
        commit(
            'resetErrors',
            errorName,
        );
    },
    resetAllErrors({commit})
    {
        commit('resetAllErrors');
    },
    validate({
        commit,
        state,
    }, {
        value,
        errorName,
        filterName,
        context = {},
    })
    {
        let formFilter: any = state.filter[filterName]; // TODO SPA replace any to interface filter construct
        if (formFilter)
        {
            let filteredValue = value;
            let errors        = [];

            if (formFilter.filters)
            {
                for (let filter of formFilter.filters)
                {
                    filteredValue = executeFilter(filter.name, filteredValue, filter.options);
                }
            }

            if (formFilter.validators && (formFilter.required || value || formFilter.validators.filter((validator: { name: string }) => validator.name === 'NotEmpty').length))
            {
                for (let validator of formFilter.validators)
                {
                    let result = executeValidator(validator.name, filteredValue, validator.options ? deepCopyObject(validator.options) : validator.options, context);

                    if (!result.validate && result.error)
                    {
                        errors.push(result.error);

                        if (validator.break_chain_on_failure)
                        {
                            break;
                        }
                    }
                }
            }

            commit(
                'setError',
                {
                    errorName,
                    errors,
                },
            );
        }
    },
    reset({commit})
    {
        commit('reset');
    },
    swapErrors({commit}, {
        from,
        to,
    })
    {
        commit(
            'swapErrors',
            {
                from,
                to,
            },
        );
    },
    setErrorsAndValidated({commit}, {
        errorName,
        errors,
        validated,
    })
    {
        commit(
            'setErrorsAndValidated',
            {
                errorName,
                errors,
                validated,
            },
        );
    },
};

export default actions;